import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen, Briefcase, GraduationCap, PenTool } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const About = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://lauranovakwrites.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": "https://lauranovakwrites.com/about"
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="About Laura Novak - Journalist Turned Novelist"
        description="From The New York Times newsroom to fiction writing. Learn about Laura Novak's journey from award-winning journalist to comedic mystery novelist, covering 25+ years of storytelling."
        keywords="Laura Novak bio, journalist author, NYT writer, mystery author biography, California journalist, fiction writer background"
        url="https://lauranovakwrites.com/about"
        ogImage="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/4aprzk88_DTLAskyline.jpg"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-slate-900">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/xb4a6t1h_laura-hat.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900/90"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            About Laura
          </h1>
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-16 bg-amber-400"></div>
            <PenTool className="w-6 h-6 text-amber-400" />
            <div className="h-px w-16 bg-amber-400"></div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-20">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-12 text-center animate-fade-in-up">
              A Life Writing Stories
            </h2>
            
            <div className="max-w-6xl mx-auto">
              <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed">
                {/* Harvard photo - floats left on desktop */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/i0izgioj_laura-max.png" 
                  alt="Laura Novak at Harvard Law School graduation with son Max" 
                  className="md:float-left md:w-[45%] md:mr-8 md:mb-6 w-full mb-8 rounded-lg shadow-2xl"
                />
                
                <p className="text-lg mb-6">
                  My first byline appeared in my Massachusetts high school newspaper. Then college in Morningside Heights in New York City became my beat for <em>The Barnard Bulletin</em>. My first broadcast? An overnight feed from ABC News in London, where I worked as the David Jayne Fellow after graduation.
                </p>
                
                <p className="text-lg mb-6">
                  Throughout graduate school at Columbia and beyond, I worked for ABC News in New York before landing in San Francisco with one suitcase and $500 to my name. I practically <em>was</em> Mary Ann Singleton from <em>Tales of the City</em> – a favorite novel I have tried to emulate with my newest fiction. Life has come full circle!
                </p>

                {/* Pull Quote */}
                <div className="my-12 py-10 px-8 bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-r-lg shadow-lg">
                  <p className="text-2xl md:text-3xl font-serif italic text-slate-800 leading-relaxed text-center">
                    "I was Mary Ann Singleton personified. And now I've written my own modern day version of Armistead Maupin's <em>Tales of the City</em> meets Prime TV's <em>The Outlaws</em>."
                  </p>
                </div>
                
                <p className="text-lg mb-6">
                  A job as a news writer and editor at KCBS Radio got me into the performer's union and allowed me to file stories for the network. That was a big deal in those days! That led to an on-air reporter/anchor job in Sacramento — where I sat alongside a guy named Rush Limbaugh. Returning to San Francisco, I worked in Public Relations for a multi-national corporation. From there, I became an on-camera street reporter for Fox, CBS, and NBC. General assignment was my beat, but crime was my specialty.
                </p>
                
                <p className="text-lg mb-6">
                  My print journalism career took off after my husband and I had our son. His complex medical life led me to pediatric feature stories — a welcome pivot from live shots and intense deadlines. I reported for <span className="font-semibold text-slate-900">The New York Times</span>, where my essays made the most-emailed lists, covering everything from business and health to art and food across California.
                </p>
                
                {/* Couple photo - floats right, positioned lower in content */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/ggptnkov_laura-mark.png" 
                  alt="Laura Novak and husband Mark" 
                  className="md:float-right md:w-[32%] md:ml-8 md:mb-6 w-full my-8 rounded-lg shadow-2xl"
                />
                
                <p className="text-lg mb-6">
                  Then came fiction. I rode the Amazon Select wave and uploaded <em>Finding Clarity</em> to high results and fun reviews. Scribd named me their most-read female author, earning me an invitation to read at <a href="https://www.litquake.org/" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:text-indigo-900 font-semibold underline">LitQuake</a> in San Francisco. <em>Murder at the Mailbox</em> soon followed with its deep dive into the kooky world of Berkeley.
                </p>
                
                <p className="text-lg mb-6">
                  As our son launched into the world — off to Harvard Law School — my newest novel took shape from the silly characters and crazy stories I encountered in Downtown LA. My goal now? To see Nora and her nutty escapades land in the hands of a publisher.
                </p>
                
                <div className="clear-both"></div>
              </div>
            </div>
          </div>

          {/* Career Highlights */}
          <div className="mb-20">
            <h2 className="text-4xl font-serif font-bold text-slate-900 mb-12 text-center">
              Career Highlights
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-start">
              {/* TV Reporting Photo - Left Side */}
              <div className="order-2 md:order-1">
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/aj7c872h_IMG_9832.JPG" 
                    alt="Laura Novak TV reporting for KTVU" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Three Boxes - Right Side */}
              <div className="order-1 md:order-2 space-y-6">
                {/* Education Box */}
                <Card className="border-2 hover:border-amber-500 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-amber-100 p-3 rounded-lg">
                        <GraduationCap className="w-8 h-8 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Education</h3>
                        <div className="space-y-3 text-slate-600 leading-relaxed">
                          <div>
                            <p className="font-semibold text-slate-800">Barnard College, Columbia University</p>
                            <p className="text-sm">BA</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">Leningrad State University, USSR</p>
                            <p className="text-sm">Diploma with Honors</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">Graduate School of Journalism, Columbia University</p>
                            <p className="text-sm">MS</p>
                          </div>
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="font-semibold text-amber-700">David Jayne Fellow, ABC News</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Broadcast Journalism Box */}
                <Card className="border-2 hover:border-amber-500 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-amber-100 p-3 rounded-lg">
                        <Briefcase className="w-8 h-8 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Broadcast Journalism</h3>
                        <p className="text-slate-600 leading-relaxed">
                          Worked for ABC in London and New York, CBS radio in San Francisco, 
                          KFBK radio in Sacramento, and various TV stations including CBS, NBC, and Fox 
                          in San Francisco and Oakland. Known as the "blood and guts reporter" for 
                          specializing in crime stories.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* The New York Times Box */}
                <Card className="border-2 hover:border-amber-500 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-amber-100 p-3 rounded-lg">
                        <PenTool className="w-8 h-8 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">The New York Times</h3>
                        <p className="text-slate-600 leading-relaxed">
                          Ten years contributing features and articles on diverse topics including business, health, 
                          arts, and non-profits. Covered unique stories ranging from art conservation 
                          projects and innovative community efforts to business strategies and medical mysteries.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* The Journey to Fiction */}
          <div className="bg-slate-50 rounded-2xl p-12 mb-20">
            <h2 className="text-4xl font-serif font-bold text-slate-900 mb-8 text-center">
              The Journey to Fiction
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-slate-700 leading-relaxed">
              <p>
                After 25 years of journalism, I found myself drawn to a different kind of storytelling. 
                The mysteries I'd covered as a crime reporter — the questions left unanswered, the human 
                stories behind the headlines — inspired me to create my own narratives.
              </p>
              <p>
                First came <span className="font-semibold text-slate-900">Finding Clarity</span>, 
                introducing detective Clari Drake in a story set against the backdrop of Berkeley's 
                unique culture and politics. The book blends my journalistic eye for detail with the 
                creative freedom of fiction.
              </p>
              <p>
                Then came <span className="font-semibold text-slate-900">Murder at the Mailbox</span>, 
                where Clari returns to investigate a crime that hits close to home.
              </p>
              <p>
                Now, I'm developing a new character, Nora, who's making waves in Los Angeles. 
                She's ready for her close-up, and I can't wait to share her story.
              </p>
            </div>
          </div>

          {/* From the Field - Vintage Photo Section */}
          <div className="mb-20">
            <div className="bg-slate-900 rounded-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-[600px] md:h-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_laura-nova-author/artifacts/fmyw3q1b_IMG_0639.png"
                    alt="Laura Novak as a young journalist in the field"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/30"></div>
                </div>
                <div className="p-12 flex items-center">
                  <div>
                    <div className="inline-block bg-amber-400/20 px-4 py-2 rounded-full mb-6">
                      <p className="text-amber-400 font-semibold text-sm">FROM THE FIELD</p>
                    </div>
                    <h2 className="text-4xl font-serif font-bold text-white mb-6">
                      The Blood and Guts Reporter
                    </h2>
                    <p className="text-slate-300 text-lg leading-relaxed mb-6">
                      With tape recorder in hand and notebook ready, I covered breaking news, crime scenes, and the human stories behind them — from New York and London to San Francisco and Los Angeles.
                    </p>
                    <p className="text-slate-400 leading-relaxed">
                      Those years in the field — chasing stories, interviewing sources, and 
                      piecing together truth from chaos — taught me everything I know about 
                      narrative, character, and the compelling power of a good story.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-6">
              Explore My Work
            </h2>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              From journalism to mystery novels, discover the stories I've told
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/books">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                  <BookOpen className="w-5 h-5 mr-2" />
                  My Books
                </Button>
              </Link>
              <Link to="/news">
                <Button size="lg" variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-8">
                  News & Articles
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Decorative Separator before Footer */}
      <div className="h-2 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60"></div>
    </div>
  );
};

export default About;